import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Eye, Plus } from 'lucide-react';
import {
  EyeExaminationDisplayStatus,
  EyeExaminationStatus,
  PERMISSIONS,
  type EyeExaminationSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/utils/format';
import { useCustomerExaminationsQuery } from '../api/medical.api';
import { isolate } from '../bidi';
import { ExaminationStatusBadge } from './ExaminationStatusBadge';

/**
 * A customer's eye examinations on their profile.
 *
 * Not rendered at all without medical.view, so no clinical request is made for someone who may not see the answer. The list carries no clinical value — number, date, status, examiner, branch and whether a prescription was issued. Replaced, voided and discarded records stay listed, because nothing clinical leaves the history.
 */
export function EyeExaminationHistory({ customerId }: { customerId: string }) {
  const { can } = usePermissions();
  if (!can(PERMISSIONS.MEDICAL_VIEW)) return null;
  return <History customerId={customerId} canCreate={can(PERMISSIONS.MEDICAL_CREATE)} />;
}

function History({ customerId, canCreate }: { customerId: string; canCreate: boolean }) {
  const { t } = useTranslation();
  const { data, isPending, isError } = useCustomerExaminationsQuery(customerId, true);
  const items = data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title={t('medical.title')}
        subtitle={t('medical.history.subtitle')}
        wrapAction
        action={
          canCreate ? (
            <Link
              to={`/customers/${customerId}/eye-examinations/new`}
              className="bg-brand-600 hover:bg-brand-700 inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium whitespace-nowrap text-white"
            >
              <Plus className="size-4" aria-hidden />
              {t('medical.history.new')}
            </Link>
          ) : null
        }
      />

      {isError ? (
        <div className="p-4">
          <Alert tone="error">{t('medical.history.loadError')}</Alert>
        </div>
      ) : isPending ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Eye}
          title={t('medical.history.empty')}
          description={t('medical.history.emptyHint')}
        />
      ) : (
        <ul className="divide-line divide-y">
          {items.map((examination) => (
            <ExaminationRow
              key={examination.id}
              customerId={customerId}
              examination={examination}
              relations={relationsOf(examination, items)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

interface Relation {
  key: string;
  number: string;
}

/** How a record relates to the others listed: what it amends or replaces, what replaced it, and an amendment still open. A record outside this page of the list is simply not named. */
function relationsOf(
  examination: EyeExaminationSummary,
  items: EyeExaminationSummary[],
): Relation[] {
  const numberOf = (id: string | null) =>
    id ? items.find((item) => item.id === id)?.examinationNumber : undefined;
  const relations: Relation[] = [];

  const replaced = numberOf(examination.replacesExaminationId);
  if (replaced) {
    const key =
      examination.displayStatus === EyeExaminationDisplayStatus.DRAFT
        ? 'medical.history.amends'
        : examination.displayStatus === EyeExaminationDisplayStatus.DISCARDED
          ? 'medical.history.discardedAmendment'
          : 'medical.history.replaces';
    relations.push({ key, number: replaced });
  }

  const replacement = numberOf(examination.replacedByExaminationId);
  if (replacement) relations.push({ key: 'medical.history.replacedBy', number: replacement });

  if (examination.status === EyeExaminationStatus.COMPLETED) {
    const open = items.find(
      (item) =>
        item.replacesExaminationId === examination.id && item.status === EyeExaminationStatus.DRAFT,
    );
    if (open) {
      relations.push({
        key: 'medical.history.amendmentInProgress',
        number: open.examinationNumber,
      });
    }
  }
  return relations;
}

function ExaminationRow({
  customerId,
  examination,
  relations,
}: {
  customerId: string;
  examination: EyeExaminationSummary;
  relations: Relation[];
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  // The details keep a working width: on a narrow card the status, prescription and link move below them together instead of squeezing the date and record numbers into a sliver.
  return (
    <li className="flex flex-wrap items-center gap-3 p-4">
      <span className="min-w-0 grow basis-48">
        <span className="text-ink flex flex-wrap items-center gap-2 font-medium">
          <span dir="ltr" className="font-mono text-sm whitespace-nowrap">
            {examination.examinationNumber}
          </span>
          <span className="text-muted text-sm font-normal">
            {formatDate(examination.examinedAt, locale)}
          </span>
        </span>
        <span className="text-muted block text-xs">
          {examination.examinerName ?? t('medical.history.examinerUnassigned')}
          {examination.branchName ? ` · ${examination.branchName}` : ''}
        </span>
        {relations.map((relation) => (
          <span key={relation.key} className="text-ink block text-xs">
            {t(relation.key, { number: isolate(relation.number) })}
          </span>
        ))}
      </span>

      <span className="flex flex-wrap items-center gap-3">
        <ExaminationStatusBadge status={examination.displayStatus} />

        {examination.prescriptionNumber ? (
          <Badge tone="brand">
            <span dir="ltr" className="font-mono whitespace-nowrap">
              {examination.prescriptionNumber}
            </span>
          </Badge>
        ) : (
          <span className="text-muted text-xs">
            {examination.hasPrescription
              ? t('medical.history.prescriptionNotIssued')
              : t('medical.history.noPrescription')}
          </span>
        )}

        <Link
          to={`/customers/${customerId}/eye-examinations/${examination.id}`}
          aria-label={t('medical.history.viewExamination', {
            number: examination.examinationNumber,
          })}
          className="text-brand-600 hover:text-brand-700 text-sm font-medium"
        >
          {t('medical.history.view')}
        </Link>
      </span>
    </li>
  );
}
